import { TextField } from "@/app/fields";
import { Code, Form } from "@/ui";
import { TTL_MAX, TTL_MIN, type ChannelErrors, type TelegramDraft } from "./channelDraft";

interface Props { value: TelegramDraft; errors: ChannelErrors; isNew: boolean; onChange: (v: TelegramDraft) => void }

/** Telegram 通道的连接参数，键同 internal/httpcfg 的 Telegram（docs/03 3.5，D207 第 2 条）；base_url 一般不改，放在"高级"栏（TelegramBaseURL）。
 * API token 编辑时显示打码值，留空或保持打码即不改（D43），与 SMPP 密码一致。 */
export function TelegramFields({ value, errors, isNew, onChange }: Props) {
  const field = (k: keyof TelegramDraft) => (v: string) => onChange({ ...value, [k]: v });
  return (
    <Form columns={2}>
      <TextField id="ch-tg-token" label="API token" required={isNew} mono value={value.token} error={errors["telegram.token"]}
        hint={isNew ? "Telegram Gateway 后台的 API token" : "显示为 **** 时保持不动或留空即不修改"} onChange={field("token")} />
      <TextField id="ch-tg-ttl" label="TTL（秒）" type="number" mono placeholder="不设" value={value.ttl} error={errors["telegram.ttl"]}
        hint={`${TTL_MIN} 到 ${TTL_MAX}；留空不设`} help="超时仍未送达视作失败，Telegram 会退款。设了 TTL 时，受理后 TTL + 30 秒仍没有送达回执的消息按过期失败收尾，不计成本"
        onChange={field("ttl")} />
      <TextField id="ch-tg-code-pattern" label="取码规则" mono placeholder="缺省：第一串 4–8 位数字" value={value.code_pattern} error={errors["telegram.code_pattern"]}
        hint="正则；有捕获组取第一组，留空用缺省"
        help={<>缺省取原文里第一串前后不接数字的 4–8 位数字，原文本身不发给 Telegram。原文先出现年份、订单号时要按模板指定，例如 <Code>{"code[: ]+(\\d+)"}</Code> 从“2024 order 7788, code: 5521”取出 5521。取不到 4–8 位数字的消息不发出，按永久失败处理</>}
        onChange={field("code_pattern")} />
      <TextField id="ch-tg-sender" label="sender_username" mono value={value.sender_username} error={errors["telegram.sender_username"]}
        hint="可选；已认证的 Telegram 频道" help="以哪个已认证频道的名义发码，留空用 Telegram 的缺省发送方" onChange={field("sender_username")} />
      <TextField id="ch-tg-timeout" label="请求超时" mono placeholder="默认 10s" value={value.timeout} error={errors["telegram.timeout"]}
        hint="单次请求的期限，例如 10s；留空取默认" onChange={field("timeout")} />
    </Form>
  );
}

/** Telegram Gateway 的地址，只在"高级"栏：预览与测试由环境变量统一覆盖，生产一般不改（D209 第 9 条）。 */
export function TelegramBaseURL({ value, errors, onChange }: Omit<Props, "isNew">) {
  return (
    <Form>
      <TextField id="ch-tg-base-url" label="Gateway 地址" mono placeholder="默认 https://gatewayapi.telegram.org/" value={value.base_url} error={errors["telegram.base_url"]}
        hint="一般不改；留空取默认" help="预览与测试环境用 AXON_TELEGRAM_BASE_URL 统一指向模拟上游，不用逐条修改" onChange={(v) => onChange({ ...value, base_url: v })} />
    </Form>
  );
}
